/* eslint-disable react-refresh/only-export-components */
/**
 * Error Boundary Components
 *
 * React error boundaries for graceful error handling throughout the application.
 * Provides user-friendly error messages and recovery options.
 *
 * @module components/ErrorBoundary
 */

import React, { Component, useCallback, useState, useEffect } from 'react';
import {
  ErrorType,
  classifyError,
  getErrorMessage,
  getRecoveryStrategy,
  errorReporter,
  formatErrorForDisplay,
} from '../../utils/errorReporting';
import type { AppError, RecoveryStrategy } from '../../utils/errorReporting';

// ============================================================================
// Types
// ============================================================================

export interface ErrorBoundaryProps {
  /** Child components to render */
  children: React.ReactNode;
  /** Custom fallback UI to render on error */
  fallback?: React.ReactNode;
  /** Custom fallback render function with error info */
  fallbackRender?: (props: FallbackProps) => React.ReactNode;
  /** Callback when an error is caught */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Keys that trigger boundary reset when they change */
  resetKeys?: unknown[];
  /** Callback before resetting */
  onReset?: () => void;
  /** Error type hint for classification */
  errorType?: ErrorType;
  /** Whether to show technical details by default */
  showDetails?: boolean;
  /** Custom error container className */
  className?: string;
}

export interface FallbackProps {
  /** The classified error */
  error: AppError;
  /** Original React error info */
  errorInfo: React.ErrorInfo | null;
  /** Reset the error boundary */
  resetErrorBoundary: () => void;
  /** Recommended recovery strategy */
  strategy: RecoveryStrategy;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: AppError | null;
  errorInfo: React.ErrorInfo | null;
}

// ============================================================================
// Main Error Boundary Class Component
// ============================================================================

/**
 * React Error Boundary that catches JavaScript errors in child component tree
 *
 * Features:
 * - Catches and classifies errors automatically
 * - Shows user-friendly error messages
 * - Provides recovery options (retry, reload, reset)
 * - Reports errors to error tracking service
 * - Supports reset via resetKeys prop
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(_error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Classify the error
    const appError = classifyError(
      error,
      this.props.errorType ?? ErrorType.RENDER_ERROR
    );

    // Add component stack to context
    appError.context = {
      ...appError.context,
      componentStack: errorInfo.componentStack,
    };

    // Update state with classified error
    this.setState({
      error: appError,
      errorInfo,
    });

    // Report to error tracking
    errorReporter.captureError(appError);
    errorReporter.addBreadcrumb('Error boundary caught error', 'error', {
      errorType: appError.type,
      errorMessage: appError.message,
    });

    // Call user's onError callback
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Reset boundary when resetKeys change
    if (this.state.hasError && this.props.resetKeys) {
      // Check for length changes or value changes in reset keys
      const prevKeys = prevProps.resetKeys ?? [];
      const currKeys = this.props.resetKeys;
      const hasResetKeyChanged =
        currKeys.length !== prevKeys.length ||
        currKeys.some((key, index) => key !== prevKeys[index]);

      if (hasResetKeyChanged) {
        this.reset();
      }
    }
  }

  reset = (): void => {
    this.props.onReset?.();
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    errorReporter.addBreadcrumb('Error boundary reset', 'user');
  };

  render(): React.ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, fallbackRender, className, showDetails } =
      this.props;

    if (hasError && error) {
      const strategy = getRecoveryStrategy(error);

      // Custom fallback render function
      if (fallbackRender) {
        return fallbackRender({
          error,
          errorInfo,
          resetErrorBoundary: this.reset,
          strategy,
        });
      }

      // Custom fallback element
      if (fallback) {
        return fallback;
      }

      // Default fallback UI
      return (
        <DefaultErrorFallback
          error={error}
          errorInfo={errorInfo}
          resetErrorBoundary={this.reset}
          strategy={strategy}
          className={className}
          showDetailsDefault={showDetails}
        />
      );
    }

    return children;
  }
}

// ============================================================================
// Default Error Fallback Component
// ============================================================================

interface DefaultErrorFallbackProps extends FallbackProps {
  className?: string;
  showDetailsDefault?: boolean;
}

/**
 * Default fallback UI shown when an error occurs
 */
function DefaultErrorFallback({
  error,
  resetErrorBoundary,
  strategy,
  className = '',
  showDetailsDefault = false,
}: DefaultErrorFallbackProps): React.ReactElement {
  const [showDetails, setShowDetails] = useState(showDetailsDefault);

  const { title, message, details, action } = formatErrorForDisplay(error);

  const handleAction = useCallback(() => {
    switch (strategy) {
      case 'retry':
      case 'reset':
        resetErrorBoundary();
        break;
      case 'reload':
        if (typeof window !== 'undefined') {
          window.location.reload();
        }
        break;
      case 'contact_support':
        // Could open a support form or mailto link
        if (typeof window !== 'undefined') {
          window.open('mailto:admin@writerslogic.com?subject=Error Report', '_blank');
        }
        break;
    }
  }, [strategy, resetErrorBoundary]);

  return (
    <div
      className={`error-boundary-container ${className}`}
      role="alert"
      aria-live="assertive"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        minHeight: '200px',
        backgroundColor: 'var(--panel-bg, #1a1a2e)',
        borderRadius: '8px',
        border: '1px solid var(--border-color, #333)',
        color: 'var(--editor-text, #e0e0e0)',
        textAlign: 'center',
      }}
    >
      {/* Error Icon */}
      <div
        style={{
          fontSize: '3rem',
          marginBottom: '1rem',
          opacity: 0.8,
        }}
        aria-hidden="true"
      >
        &#x26A0;
      </div>

      {/* Title */}
      <h2
        style={{
          fontSize: '1.25rem',
          fontWeight: 600,
          marginBottom: '0.5rem',
          color: 'hsl(0, 70%, 60%)',
        }}
      >
        {title}
      </h2>

      {/* User-friendly message */}
      <p
        style={{
          fontSize: '0.95rem',
          marginBottom: '1.5rem',
          maxWidth: '400px',
          lineHeight: 1.5,
          opacity: 0.85,
        }}
      >
        {message}
      </p>

      {/* Action buttons */}
      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          marginBottom: '1rem',
        }}
      >
        <button
          type="button"
          onClick={handleAction}
          style={{
            padding: '0.5rem 1.25rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: 'var(--editor-text, #fff)',
            backgroundColor: 'hsl(220, 60%, 50%)',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'background-color 0.15s',
          }}
          onMouseOver={(e) =>
            (e.currentTarget.style.backgroundColor = 'hsl(220, 60%, 45%)')
          }
          onMouseOut={(e) =>
            (e.currentTarget.style.backgroundColor = 'hsl(220, 60%, 50%)')
          }
        >
          {action}
        </button>

        {strategy !== 'reload' && (
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '0.5rem 1.25rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'var(--editor-text, #e0e0e0)',
              backgroundColor: 'transparent',
              border: '1px solid var(--border-color, #444)',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'background-color 0.15s',
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)')
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = 'transparent')
            }
          >
            Reload Page
          </button>
        )}
      </div>

      {/* Technical details (collapsible) */}
      <details
        style={{
          marginTop: '1rem',
          width: '100%',
          maxWidth: '500px',
          textAlign: 'left',
        }}
        open={showDetails}
        onToggle={(e) =>
          setShowDetails((e.target as HTMLDetailsElement).open)
        }
      >
        <summary
          style={{
            cursor: 'pointer',
            fontSize: '0.8rem',
            opacity: 0.6,
            userSelect: 'none',
            marginBottom: '0.5rem',
          }}
        >
          Technical Details
        </summary>
        <pre
          style={{
            fontSize: '0.75rem',
            padding: '0.75rem',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '4px',
            overflow: 'auto',
            maxHeight: '200px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            margin: 0,
          }}
        >
          <strong>Type:</strong> {error.type}
          {'\n'}
          <strong>Message:</strong> {details}
          {'\n'}
          <strong>ID:</strong> {error.id}
          {error.stack && (
            <>
              {'\n\n'}
              <strong>Stack Trace:</strong>
              {'\n'}
              {error.stack}
            </>
          )}
        </pre>
      </details>
    </div>
  );
}

// ============================================================================
// Specialized Error Boundaries
// ============================================================================

/**
 * Error boundary specifically for the analysis panel
 * Provides analysis-specific error messages and reset functionality
 */
export function AnalysisErrorBoundary({
  children,
  onReset,
}: {
  children: React.ReactNode;
  onReset?: () => void;
}): React.ReactElement {
  return (
    <ErrorBoundary
      errorType={ErrorType.ANALYSIS_ERROR}
      onReset={onReset}
      fallbackRender={({ error, resetErrorBoundary }) => (
        <div
          style={{
            padding: '1.5rem',
            backgroundColor: 'var(--panel-bg, #1a1a2e)',
            borderRadius: '8px',
            border: '1px solid hsl(0, 50%, 40%)',
            color: 'var(--editor-text, #e0e0e0)',
          }}
          role="alert"
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1rem',
            }}
          >
            <span
              style={{ fontSize: '1.5rem', color: 'hsl(0, 70%, 60%)' }}
              aria-hidden="true"
            >
              &#x26A0;
            </span>
            <h3
              style={{
                margin: 0,
                fontSize: '1rem',
                fontWeight: 600,
                color: 'hsl(0, 70%, 60%)',
              }}
            >
              Analysis Error
            </h3>
          </div>

          <p
            style={{
              fontSize: '0.875rem',
              marginBottom: '1rem',
              opacity: 0.85,
            }}
          >
            {getErrorMessage(error)}
          </p>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              onClick={resetErrorBoundary}
              style={{
                padding: '0.4rem 1rem',
                fontSize: '0.8rem',
                color: 'var(--editor-text, #fff)',
                backgroundColor: 'hsl(220, 60%, 50%)',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Clear & Retry
            </button>
          </div>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * Lightweight error boundary for individual components
 * Shows inline error indicator without breaking the layout
 */
export function ComponentErrorBoundary({
  children,
  componentName,
}: {
  children: React.ReactNode;
  componentName?: string;
}): React.ReactElement {
  return (
    <ErrorBoundary
      errorType={ErrorType.RENDER_ERROR}
      fallbackRender={({ error: _error, resetErrorBoundary }) => (
        <div
          style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'hsla(0, 50%, 50%, 0.1)',
            borderRadius: '4px',
            border: '1px solid hsla(0, 50%, 50%, 0.3)',
            color: 'var(--editor-text, #e0e0e0)',
            fontSize: '0.8rem',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
          role="alert"
        >
          <span style={{ color: 'hsl(0, 70%, 60%)' }} aria-hidden="true">
            &#x26A0;
          </span>
          <span>
            {componentName ? `${componentName} failed to load` : 'Component error'}
          </span>
          <button
            type="button"
            onClick={resetErrorBoundary}
            style={{
              padding: '0.2rem 0.5rem',
              fontSize: '0.7rem',
              color: 'inherit',
              backgroundColor: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '3px',
              cursor: 'pointer',
              marginLeft: '0.25rem',
            }}
          >
            Retry
          </button>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

// ============================================================================
// Hook for Functional Components
// ============================================================================

/**
 * Hook to manually trigger error boundary
 * Useful for catching async errors that React won't catch automatically
 */
export function useErrorHandler(): (error: Error) => void {
  const [, setError] = useState<Error | null>(null);

  return useCallback((error: Error) => {
    setError(() => {
      throw error;
    });
  }, []);
}

/**
 * Hook to catch and display async errors
 * Returns error state and a wrapper function
 */
export function useAsyncError(): {
  error: AppError | null;
  clearError: () => void;
  handleError: (error: unknown, type?: ErrorType) => void;
  wrapAsync: <T>(
    promise: Promise<T>,
    errorType?: ErrorType
  ) => Promise<T | undefined>;
} {
  const [error, setError] = useState<AppError | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleError = useCallback((err: unknown, type?: ErrorType) => {
    const appError = classifyError(err, type);
    setError(appError);
    errorReporter.captureError(appError);
  }, []);

  const wrapAsync = useCallback(
    async <T,>(
      promise: Promise<T>,
      errorType?: ErrorType
    ): Promise<T | undefined> => {
      try {
        return await promise;
      } catch (err) {
        handleError(err, errorType);
        return undefined;
      }
    },
    [handleError]
  );

  return { error, clearError, handleError, wrapAsync };
}

// ============================================================================
// Error Display Components
// ============================================================================

/**
 * Inline error message component
 * For displaying non-fatal errors without blocking the UI
 */
export function InlineError({
  error,
  onDismiss,
  onRetry,
  className = '',
}: {
  error: AppError;
  onDismiss?: () => void;
  onRetry?: () => void;
  className?: string;
}): React.ReactElement {
  const message = getErrorMessage(error);

  return (
    <div
      className={className}
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.75rem 1rem',
        backgroundColor: 'hsla(0, 50%, 50%, 0.1)',
        borderRadius: '6px',
        border: '1px solid hsla(0, 50%, 50%, 0.3)',
        color: 'var(--editor-text, #e0e0e0)',
        fontSize: '0.875rem',
      }}
    >
      <span style={{ color: 'hsl(0, 70%, 60%)' }} aria-hidden="true">
        &#x26A0;
      </span>
      <span style={{ flex: 1 }}>{message}</span>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          style={{
            padding: '0.25rem 0.75rem',
            fontSize: '0.75rem',
            color: 'var(--editor-text, #fff)',
            backgroundColor: 'hsl(220, 60%, 50%)',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          style={{
            padding: '0.25rem',
            fontSize: '1rem',
            lineHeight: 1,
            color: 'inherit',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            opacity: 0.5,
          }}
          aria-label="Dismiss error"
        >
          x
        </button>
      )}
    </div>
  );
}

/**
 * Toast-style error notification
 * Auto-dismisses after a timeout
 */
export function ErrorToast({
  error,
  onDismiss,
  autoDismissMs = 5000,
}: {
  error: AppError;
  onDismiss: () => void;
  autoDismissMs?: number;
}): React.ReactElement {
  useEffect(() => {
    if (autoDismissMs > 0) {
      const timer = setTimeout(onDismiss, autoDismissMs);
      return () => clearTimeout(timer);
    }
  }, [autoDismissMs, onDismiss]);

  const message = getErrorMessage(error);

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        bottom: '1.5rem',
        right: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.875rem 1.25rem',
        backgroundColor: 'hsl(0, 40%, 25%)',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        color: 'var(--editor-text, #fff)',
        fontSize: '0.875rem',
        maxWidth: '400px',
        animation: 'slideIn 0.2s ease-out',
        zIndex: 9998,
      }}
    >
      <span aria-hidden="true">&#x26A0;</span>
      <span style={{ flex: 1 }}>{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        style={{
          padding: '0.25rem',
          fontSize: '1rem',
          lineHeight: 1,
          color: 'inherit',
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          opacity: 0.7,
        }}
        aria-label="Dismiss"
      >
        x
      </button>
    </div>
  );
}

// ============================================================================
// Global Error Handler Setup
// ============================================================================

/**
 * Set up global error handlers for uncaught errors
 * Call once during app initialization
 */
export function setupGlobalErrorHandlers(): () => void {
  const handleUnhandledError = (event: ErrorEvent) => {
    const appError = classifyError(event.error || event.message);
    errorReporter.captureError(appError);
    errorReporter.addBreadcrumb('Unhandled error', 'error', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  };

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const appError = classifyError(event.reason);
    errorReporter.captureError(appError);
    errorReporter.addBreadcrumb('Unhandled promise rejection', 'error', {
      reason: String(event.reason),
    });
  };

  window.addEventListener('error', handleUnhandledError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  // Return cleanup function
  return () => {
    window.removeEventListener('error', handleUnhandledError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  };
}

// ============================================================================
// Exports
// ============================================================================

export default ErrorBoundary;
